<template>
  <div>
    <Header />
  
  <v-container fluid>
    <v-row>
      <!-- Create Message Section -->
      <v-col cols="12" lg="6">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-message-plus</v-icon>
            Neue Nachricht erstellen
          </v-card-title>

          <v-card-text>
            <v-form ref="messageForm" v-model="formValid">
              <!-- Title -->
              <v-text-field
                v-model="newMessage.title"
                label="Titel"
                :rules="[v => !!v || 'Titel ist erforderlich']"
                outlined
                dense
                class="mb-3"
              ></v-text-field>

              <!-- Content Editor -->
              <div class="mb-3">
                <label class="v-label theme--light">Inhalt</label>
                <div class="editor-toolbar">
                  <!-- Text Formatting -->
                  <v-btn-toggle
                    v-model="formatting"
                    multiple
                    dense
                    class="mr-2"
                  >
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn small @click="formatText('bold')" v-bind="attrs" v-on="on">
                          <v-icon small>mdi-format-bold</v-icon>
                        </v-btn>
                      </template>
                      <span>Fett (Ctrl+B)</span>
                    </v-tooltip>
                    
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn small @click="formatText('italic')" v-bind="attrs" v-on="on">
                          <v-icon small>mdi-format-italic</v-icon>
                        </v-btn>
                      </template>
                      <span>Kursiv (Ctrl+I)</span>
                    </v-tooltip>
                    
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn small @click="formatText('underline')" v-bind="attrs" v-on="on">
                          <v-icon small>mdi-format-underline</v-icon>
                        </v-btn>
                      </template>
                      <span>Unterstrichen (Ctrl+U)</span>
                    </v-tooltip>
                    
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn small @click="formatText('strikeThrough')" v-bind="attrs" v-on="on">
                          <v-icon small>mdi-format-strikethrough</v-icon>
                        </v-btn>
                      </template>
                      <span>Durchgestrichen</span>
                    </v-tooltip>
                  </v-btn-toggle>

                  <v-divider vertical class="mx-2"></v-divider>

                  <!-- Headings -->
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn small v-bind="attrs" v-on="on" class="mr-1">
                        <v-icon small left>mdi-format-header-pound</v-icon>
                        Überschrift
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item @click="formatHeading('h3')">
                        <v-list-item-title>Überschrift 1</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="formatHeading('h4')">
                        <v-list-item-title>Überschrift 2</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="formatHeading('p')">
                        <v-list-item-title>Normal</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>

                  <v-divider vertical class="mx-2"></v-divider>

                  <!-- Lists -->
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn small @click="insertList('ul')" class="mr-1" v-bind="attrs" v-on="on">
                        <v-icon small>mdi-format-list-bulleted</v-icon>
                      </v-btn>
                    </template>
                    <span>Aufzählungsliste</span>
                  </v-tooltip>
                  
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn small @click="insertList('ol')" class="mr-1" v-bind="attrs" v-on="on">
                        <v-icon small>mdi-format-list-numbered</v-icon>
                      </v-btn>
                    </template>
                    <span>Nummerierte Liste</span>
                  </v-tooltip>

                  <v-divider vertical class="mx-2"></v-divider>

                  <!-- Alignment -->
                  <v-btn-toggle
                    v-model="alignment"
                    exclusive
                    dense
                    class="mr-2"
                  >
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn small value="left" @click="formatAlign('justifyLeft')" v-bind="attrs" v-on="on">
                          <v-icon small>mdi-format-align-left</v-icon>
                        </v-btn>
                      </template>
                      <span>Linksbündig</span>
                    </v-tooltip>
                    
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn small value="center" @click="formatAlign('justifyCenter')" v-bind="attrs" v-on="on">
                          <v-icon small>mdi-format-align-center</v-icon>
                        </v-btn>
                      </template>
                      <span>Zentriert</span>
                    </v-tooltip>
                    
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn small value="right" @click="formatAlign('justifyRight')" v-bind="attrs" v-on="on">
                          <v-icon small>mdi-format-align-right</v-icon>
                        </v-btn>
                      </template>
                      <span>Rechtsbündig</span>
                    </v-tooltip>
                  </v-btn-toggle>

                  <v-divider vertical class="mx-2"></v-divider>

                  <!-- Links and Special -->
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn small @click="insertLink" class="mr-1" v-bind="attrs" v-on="on">
                        <v-icon small>mdi-link</v-icon>
                      </v-btn>
                    </template>
                    <span>Link einfügen</span>
                  </v-tooltip>

                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn small @click="insertHorizontalRule" class="mr-1" v-bind="attrs" v-on="on">
                        <v-icon small>mdi-minus</v-icon>
                      </v-btn>
                    </template>
                    <span>Horizontale Linie</span>
                  </v-tooltip>

                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn small @click="triggerImageUpload" class="mr-1" v-bind="attrs" v-on="on" :loading="uploadingImage">
                        <v-icon small>mdi-image-plus</v-icon>
                      </v-btn>
                    </template>
                    <span>Bild hochladen</span>
                  </v-tooltip>

                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn small @click="clearFormatting" class="mr-1" v-bind="attrs" v-on="on">
                        <v-icon small>mdi-format-clear</v-icon>
                      </v-btn>
                    </template>
                    <span>Formatierung entfernen</span>
                  </v-tooltip>

                  <v-divider vertical class="mx-2"></v-divider>

                  <!-- Emojis -->
                  <v-menu offset-y :close-on-content-click="false">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn small v-bind="attrs" v-on="on" class="mr-1">
                        <v-icon small>mdi-emoticon-happy-outline</v-icon>
                      </v-btn>
                    </template>
                    <v-card max-width="300">
                      <v-card-text class="pa-2">
                        <div class="emoji-grid">
                          <v-btn
                            v-for="emoji in commonEmojis"
                            :key="emoji"
                            text
                            small
                            @click="insertEmoji(emoji)"
                            class="emoji-btn"
                          >
                            {{ emoji }}
                          </v-btn>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-menu>

                  <!-- Templates -->
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn small v-bind="attrs" v-on="on" class="mr-1">
                        <v-icon small left>mdi-file-document-outline</v-icon>
                        Vorlage
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item
                        v-for="template in messageTemplates"
                        :key="template.name"
                        @click="applyTemplate(template)"
                      >
                        <v-list-item-title>{{ template.name }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>

                  <v-spacer></v-spacer>

                  <!-- HTML Toggle -->
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn 
                        small 
                        @click="showHtmlEditor = !showHtmlEditor" 
                        :color="showHtmlEditor ? 'primary' : ''"
                        v-bind="attrs" 
                        v-on="on"
                        class="mr-1"
                      >
                        <v-icon small>mdi-code-tags</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ showHtmlEditor ? 'Visueller Editor' : 'HTML-Code' }}</span>
                  </v-tooltip>

                  <!-- Preview Toggle -->
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn 
                        small 
                        @click="showPreview = !showPreview" 
                        :color="showPreview ? 'primary' : ''"
                        v-bind="attrs" 
                        v-on="on"
                      >
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>Vorschau {{ showPreview ? 'ausblenden' : 'anzeigen' }}</span>
                  </v-tooltip>
                </div>
                
                <!-- Editor and Preview -->
                <v-row no-gutters>
                  <v-col :cols="showPreview ? 6 : 12">
                    <!-- Visual Editor -->
                    <div
                      v-if="!showHtmlEditor"
                      ref="contentEditor"
                      class="content-editor"
                      contenteditable="true"
                      @input="updateContent"
                      @paste="handlePaste"
                      @keydown="handleKeydown"
                      :style="{
                        backgroundColor: newMessage.backgroundColor,
                        color: newMessage.textColor
                      }"
                    ></div>
                    
                    <!-- HTML Code Editor -->
                    <v-textarea
                      v-else
                      v-model="htmlContent"
                      class="html-editor"
                      placeholder="HTML-Code hier einfügen..."
                      outlined
                      auto-grow
                      rows="10"
                      @input="updateFromHtml"
                      :style="{
                        fontFamily: 'monospace',
                        fontSize: '13px'
                      }"
                    >
                      <template v-slot:prepend-inner>
                        <v-chip x-small color="primary" text-color="white">
                          HTML
                        </v-chip>
                      </template>
                    </v-textarea>
                  </v-col>
                  
                  <v-col v-if="showPreview" cols="6">
                    <div class="preview-pane ml-2">
                      <div class="preview-header">
                        <v-icon small left>mdi-eye</v-icon>
                        Vorschau
                      </div>
                      <div 
                        class="preview-content"
                        :style="{
                          backgroundColor: newMessage.backgroundColor,
                          color: newMessage.textColor
                        }"
                        v-html="newMessage.content"
                      ></div>
                    </div>
                  </v-col>
                </v-row>

                <!-- Character Counter -->
                <div class="editor-footer mt-2">
                  <span class="text-caption grey--text">
                    {{ contentLength }} Zeichen
                  </span>
                </div>
              </div>

              <!-- Link Dialog -->
              <v-dialog v-model="linkDialog" max-width="500">
                <v-card>
                  <v-card-title>Link einfügen</v-card-title>
                  <v-card-text>
                    <v-text-field
                      v-model="linkUrl"
                      label="URL"
                      placeholder="https://example.com"
                      outlined
                      dense
                    ></v-text-field>
                    <v-text-field
                      v-model="linkText"
                      label="Anzeigetext"
                      placeholder="Link-Text"
                      outlined
                      dense
                    ></v-text-field>
                    <v-checkbox
                      v-model="linkNewTab"
                      label="In neuem Tab öffnen"
                    ></v-checkbox>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="linkDialog = false">Abbrechen</v-btn>
                    <v-btn color="primary" @click="confirmLink">Einfügen</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>

              <!-- Hidden File Input for Image Upload -->
              <input
                ref="imageUploadInput"
                type="file"
                accept="image/*"
                style="display: none"
                @change="handleImageUpload"
              />

              <!-- Importance -->
              <v-select
                v-model="newMessage.importance"
                :items="importanceOptions"
                label="Wichtigkeit"
                outlined
                dense
                class="mb-3"
              ></v-select>

              <!-- Display Options -->
              <v-row>
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="newMessage.showAsPopup"
                    label="Als Popup anzeigen"
                    hide-details
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" md="6">
                  <v-row no-gutters>
                    <v-col cols="7">
                      <v-menu
                        ref="scheduledMenu"
                        v-model="scheduledMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="scheduledDateDisplay"
                            label="Datum"
                            prepend-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            clearable
                            @click:clear="clearScheduledDateTime"
                            dense
                            outlined
                            :disabled="!enableScheduling"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="scheduledDate"
                          @input="scheduledMenu = false"
                          :min="minDate"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="5" class="pl-2">
                      <v-menu
                        ref="timeMenu"
                        v-model="timeMenu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="scheduledTime"
                            label="Uhrzeit"
                            prepend-icon="mdi-clock-outline"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            dense
                            outlined
                            :disabled="!scheduledDate || !enableScheduling"
                          ></v-text-field>
                        </template>
                        <v-time-picker
                          v-if="timeMenu"
                          v-model="scheduledTime"
                          format="24hr"
                          @click:minute="timeMenu = false"
                        ></v-time-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                  <v-checkbox
                    v-model="enableScheduling"
                    label="Zeitgesteuert senden"
                    hide-details
                    class="mt-0 pt-0"
                  ></v-checkbox>
                </v-col>
              </v-row>

              <!-- Color Customization -->
              <v-row class="mt-3">
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="bgColorMenu"
                    :close-on-content-click="false"
                    offset-y
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="newMessage.backgroundColor"
                        label="Hintergrundfarbe"
                        prepend-icon="mdi-palette"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        dense
                      >
                        <template v-slot:append>
                          <div 
                            class="color-preview"
                            :style="{ backgroundColor: newMessage.backgroundColor }"
                          ></div>
                        </template>
                      </v-text-field>
                    </template>
                    <v-color-picker
                      v-model="newMessage.backgroundColor"
                      @input="bgColorMenu = false"
                    ></v-color-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="textColorMenu"
                    :close-on-content-click="false"
                    offset-y
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="newMessage.textColor"
                        label="Textfarbe"
                        prepend-icon="mdi-format-color-text"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        dense
                      >
                        <template v-slot:append>
                          <div 
                            class="color-preview"
                            :style="{ backgroundColor: newMessage.textColor }"
                          ></div>
                        </template>
                      </v-text-field>
                    </template>
                    <v-color-picker
                      v-model="newMessage.textColor"
                      @input="textColorMenu = false"
                    ></v-color-picker>
                  </v-menu>
                </v-col>
              </v-row>

              <!-- Recipients -->
              <v-radio-group v-model="recipientType" class="mt-3">
                <v-radio label="An alle Benutzer" value="all"></v-radio>
                <v-radio label="An bestimmte Benutzer" value="specific"></v-radio>
              </v-radio-group>

              <v-select
                v-if="recipientType === 'specific'"
                v-model="newMessage.recipients"
                :items="users"
                item-text="name"
                item-value="_id"
                label="Empfänger auswählen"
                multiple
                chips
                small-chips
                deletable-chips
                outlined
                dense
              ></v-select>

              <!-- Actions -->
              <v-row class="mt-4">
                <v-col>
                  <v-btn
                    color="primary"
                    @click="sendMessage"
                    :loading="sending"
                    :disabled="!formValid || !newMessage.content"
                    block
                  >
                    <v-icon left>mdi-send</v-icon>
                    Nachricht senden
                  </v-btn>
                </v-col>
                <v-col>
                  <v-btn
                    @click="resetForm"
                    block
                    outlined
                  >
                    <v-icon left>mdi-refresh</v-icon>
                    Zurücksetzen
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Message Overview Section -->
      <v-col cols="12" lg="6">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-message-text-outline</v-icon>
            Nachrichten-Übersicht
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click="loadMessages"
              :loading="loadingMessages"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-data-table
              :headers="tableHeaders"
              :items="allMessages"
              :loading="loadingMessages"
              class="elevation-1"
              :items-per-page="10"
            >
              <!-- Title -->
              <template v-slot:item.title="{ item }">
                <div class="font-weight-medium">{{ item.title }}</div>
              </template>

              <!-- Importance -->
              <template v-slot:item.importance="{ item }">
                <v-chip
                  :color="getImportanceColor(item.importance)"
                  text-color="white"
                  x-small
                >
                  {{ getImportanceLabel(item.importance) }}
                </v-chip>
              </template>

              <!-- Recipients -->
              <template v-slot:item.recipients="{ item }">
                <v-chip x-small>
                  {{ item.recipients.includes('all') ? 'Alle' : `${item.recipients.length} Benutzer` }}
                </v-chip>
              </template>

              <!-- Read Status -->
              <template v-slot:item.readStatus="{ item }">
                <v-progress-linear
                  :value="item.readPercentage"
                  height="20"
                  :color="item.readPercentage === 100 ? 'green' : 'orange'"
                >
                  <template v-slot:default>
                    <strong>{{ item.readCount }}/{{ item.totalRecipients }}</strong>
                  </template>
                </v-progress-linear>
              </template>

              <!-- Created Date -->
              <template v-slot:item.createdAt="{ item }">
                {{ formatDate(item.createdAt) }}
              </template>

              <!-- Actions -->
              <template v-slot:item.actions="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="openMessageDetails(item)"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>Details anzeigen</span>
                </v-tooltip>
                
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="orange"
                      @click="resendMessage(item)"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small>mdi-send-check</v-icon>
                    </v-btn>
                  </template>
                  <span>Erneut senden</span>
                </v-tooltip>
                
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="red"
                      @click="deleteMessage(item)"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Löschen</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="successSnackbar"
      color="success"
      :timeout="3000"
    >
      Nachricht erfolgreich gesendet!
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="successSnackbar = false"
        >
          OK
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Message Details Modal -->
    <v-dialog v-model="detailsDialog" max-width="800">
      <v-card v-if="selectedMessage">
        <v-card-title>
          <v-icon left>mdi-message-text</v-icon>
          Nachricht Details
          <v-spacer></v-spacer>
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <!-- Message Info -->
          <v-row class="mb-4">
            <v-col cols="12" md="6">
              <div class="text-subtitle-2">Titel</div>
              <div class="text-h6">{{ selectedMessage.title }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-subtitle-2">Wichtigkeit</div>
              <v-chip
                :color="getImportanceColor(selectedMessage.importance)"
                text-color="white"
                small
              >
                {{ getImportanceLabel(selectedMessage.importance) }}
              </v-chip>
            </v-col>
          </v-row>

          <v-row class="mb-4">
            <v-col cols="12" md="6">
              <div class="text-subtitle-2">Erstellt am</div>
              <div>{{ formatDate(selectedMessage.createdAt) }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-subtitle-2">Popup-Anzeige</div>
              <v-icon :color="selectedMessage.showAsPopup ? 'green' : 'grey'">
                {{ selectedMessage.showAsPopup ? 'mdi-check-circle' : 'mdi-close-circle' }}
              </v-icon>
            </v-col>
          </v-row>

          <!-- Message Content -->
          <div class="text-subtitle-2 mb-2">Inhalt</div>
          <div 
            class="message-content pa-3 rounded"
            :style="{
              backgroundColor: selectedMessage.backgroundColor || '#f5f5f5',
              color: selectedMessage.textColor || '#000000'
            }"
            v-html="selectedMessage.content"
          ></div>

          <!-- Read Status -->
          <div class="mt-6">
            <div class="text-subtitle-2 mb-2">Lesestatus ({{ readByCount }}/{{ totalRecipientsCount }})</div>
            <v-progress-linear
              :value="readPercentage"
              height="30"
              :color="readPercentage === 100 ? 'green' : 'orange'"
              class="mb-4"
            >
              <strong>{{ readPercentage.toFixed(0) }}%</strong>
            </v-progress-linear>

            <!-- List of readers -->
            <v-expansion-panels v-if="selectedMessage.readBy && selectedMessage.readBy.length > 0">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <span>
                    <v-icon left small>mdi-account-check</v-icon>
                    Gelesen von ({{ selectedMessage.readBy.length }} Benutzer)
                  </span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-list dense>
                    <v-list-item
                      v-for="reader in selectedMessage.readBy"
                      :key="reader._id"
                      class="px-0"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ reader.userName || 'Unbekannt' }}</v-list-item-title>
                        <v-list-item-subtitle>
                          Gelesen am: {{ formatDate(reader.readAt) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <v-expansion-panel v-if="unreadUsers.length > 0">
                <v-expansion-panel-header>
                  <span>
                    <v-icon left small color="orange">mdi-account-clock</v-icon>
                    Noch nicht gelesen ({{ unreadUsers.length }} Benutzer)
                  </span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-list dense>
                    <v-list-item
                      v-for="user in unreadUsers"
                      :key="user._id"
                      class="px-0"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ user.name }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <div v-else class="text-center py-4 grey--text">
              Diese Nachricht wurde noch von niemandem gelesen.
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="orange"
            text
            @click="resendFromDetails"
          >
            <v-icon left>mdi-send-check</v-icon>
            Erneut senden
          </v-btn>
          <v-btn
            color="primary"
            text
            @click="detailsDialog = false"
          >
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Info Snackbar -->
    <v-snackbar
      v-model="infoSnackbar"
      color="info"
      :timeout="5000"
    >
      {{ infoSnackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="infoSnackbar = false"
        >
          OK
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
  </div>
</template>

<script>
import MessageService from '@/services/MessageService';
import Header from '@/views/private/header.vue';

export default {
  name: 'AdminMessageCenter',
  
  components: {
    Header
  },
  
  data() {
    return {
      formValid: false,
      sending: false,
      loadingMessages: false,
      successSnackbar: false,
      
      newMessage: {
        title: '',
        content: '',
        importance: 'medium',
        showAsPopup: false,
        backgroundColor: '#1976D2',
        textColor: '#FFFFFF',
        recipients: ['all'],
        scheduledFor: null
      },
      
      recipientType: 'all',
      formatting: [],
      alignment: 'left',
      showPreview: false,
      showHtmlEditor: false,
      htmlContent: '',
      
      scheduledMenu: false,
      scheduledDate: null,
      scheduledTime: '09:00',
      timeMenu: false,
      enableScheduling: false,
      bgColorMenu: false,
      textColorMenu: false,
      
      // Link dialog
      linkDialog: false,
      linkUrl: '',
      linkText: '',
      linkNewTab: true,

      // Image upload
      uploadingImage: false,
      
      importanceOptions: [
        { text: 'Niedrig', value: 'low' },
        { text: 'Mittel', value: 'medium' },
        { text: 'Hoch', value: 'high' },
        { text: 'Kritisch', value: 'critical' }
      ],
      
      users: [], // Will be loaded from API
      allMessages: [],
      
      tableHeaders: [
        { text: 'Titel', value: 'title', width: '25%' },
        { text: 'Wichtigkeit', value: 'importance', width: '15%' },
        { text: 'Empfänger', value: 'recipients', width: '15%' },
        { text: 'Gelesen', value: 'readStatus', width: '20%' },
        { text: 'Erstellt', value: 'createdAt', width: '15%' },
        { text: 'Aktionen', value: 'actions', width: '10%', sortable: false }
      ],
      
      // New properties for details modal
      detailsDialog: false,
      selectedMessage: null,
      
      // Info snackbar
      infoSnackbar: false,
      infoSnackbarText: '',
      
      // Emojis
      commonEmojis: [
        '😊', '👍', '👎', '❤️', '🎉', '🎁', '✅', '❌', '⚠️', '📢',
        '📅', '⏰', '🚀', '💡', '🔔', '📦', '🛠️', '📈', '📉', '🏆',
        '⭐', '🌟', '💪', '🙏', '👏', '🤝', '📍', '🔥', '💰', '🎯'
      ],
      
      // Message templates
      messageTemplates: [
        {
          name: 'Willkommen',
          title: 'Willkommen im Team!',
          content: '<h3>Herzlich Willkommen! 🎉</h3><p>Wir freuen uns, dich in unserem Team begrüßen zu dürfen.</p><p>Bei Fragen stehen wir dir jederzeit zur Verfügung.</p><p><strong>Viel Erfolg!</strong></p>'
        },
        {
          name: 'Wartungsarbeiten',
          title: 'Geplante Wartungsarbeiten',
          content: '<h4>⚠️ Wartungsankündigung</h4><p>Wir führen planmäßige Wartungsarbeiten durch:</p><ul><li><strong>Datum:</strong> [Datum einfügen]</li><li><strong>Zeit:</strong> [Zeit einfügen]</li><li><strong>Dauer:</strong> ca. [Dauer] Minuten</li></ul><p>Während dieser Zeit kann es zu Einschränkungen kommen.</p><p>Vielen Dank für Ihr Verständnis!</p>'
        },
        {
          name: 'Neue Funktion',
          title: 'Neue Funktion verfügbar',
          content: '<h4>🚀 Neue Funktion</h4><p>Wir haben eine neue Funktion für Sie bereitgestellt:</p><p><strong>[Funktionsname]</strong></p><p>[Beschreibung der Funktion]</p><hr><p>Bei Fragen oder Feedback melden Sie sich gerne bei uns!</p>'
        },
        {
          name: 'Wichtiger Hinweis',
          title: 'Wichtiger Hinweis',
          content: '<h4>📢 Wichtiger Hinweis</h4><p>[Ihre Nachricht hier]</p><p>Bitte beachten Sie diese Information.</p><p>Mit freundlichen Grüßen<br>Ihr Team</p>'
        }
      ]
    };
  },

  computed: {
    minDate() {
      return new Date().toISOString().substr(0, 10);
    },
    
    scheduledDateDisplay: {
      get() {
        if (!this.scheduledDate) return '';
        return new Date(this.scheduledDate).toLocaleDateString('de-DE');
      },
      set(val) {
        if (!val) {
          this.clearScheduledDateTime();
        }
      }
    },
    
    scheduledDateTime() {
      if (!this.scheduledDate || !this.enableScheduling) return null;
      const date = new Date(this.scheduledDate);
      if (this.scheduledTime) {
        const [hours, minutes] = this.scheduledTime.split(':');
        date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      }
      return date;
    },
    
    // New computed properties for message details
    readByCount() {
      return this.selectedMessage?.readBy?.length || 0;
    },
    
    totalRecipientsCount() {
      if (!this.selectedMessage) return 0;
      if (this.selectedMessage.recipients.includes('all')) {
        return this.users.length;
      }
      return this.selectedMessage.recipients.length;
    },
    
    readPercentage() {
      if (this.totalRecipientsCount === 0) return 0;
      return (this.readByCount / this.totalRecipientsCount) * 100;
    },
    
    unreadUsers() {
      if (!this.selectedMessage || !this.users.length) return [];
      
      const readUserIds = (this.selectedMessage.readBy || []).map(r => r.userId);
      
      if (this.selectedMessage.recipients.includes('all')) {
        return this.users.filter(u => !readUserIds.includes(u._id));
      } else {
        return this.users.filter(u => 
          this.selectedMessage.recipients.includes(u._id) && 
          !readUserIds.includes(u._id)
        );
      }
    },
    
    // New computed property for character count
    contentLength() {
      const div = document.createElement('div');
      div.innerHTML = this.newMessage.content;
      return div.textContent.length;
    }
  },

  watch: {
    recipientType(val) {
      if (val === 'all') {
        this.newMessage.recipients = ['all'];
      } else {
        this.newMessage.recipients = [];
      }
    },
    
    scheduledDateTime(val) {
      if (val && this.enableScheduling) {
        this.newMessage.scheduledFor = val.toISOString();
      } else {
        this.newMessage.scheduledFor = null;
      }
    },
    
    enableScheduling(val) {
      if (!val) {
        this.newMessage.scheduledFor = null;
      } else if (this.scheduledDate) {
        this.newMessage.scheduledFor = this.scheduledDateTime?.toISOString();
      }
    },
    
    showHtmlEditor(val) {
      if (val) {
        // Switching to HTML editor - sync HTML content
        this.htmlContent = this.newMessage.content;
      } else {
        // Switching to visual editor - update visual editor
        this.$nextTick(() => {
          if (this.$refs.contentEditor) {
            this.$refs.contentEditor.innerHTML = this.newMessage.content;
          }
        });
      }
    }
  },

  methods: {
    updateContent() {
      if (this.$refs.contentEditor) {
        this.newMessage.content = this.$refs.contentEditor.innerHTML;
      }
    },
    
    updateFromHtml() {
      this.newMessage.content = this.htmlContent;
    },
    
    handlePaste(e) {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);
    },
    
    formatText(command) {
      document.execCommand(command, false, null);
      this.$refs.contentEditor.focus();
      this.updateContent();
    },
    
    formatHeading(tag) {
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      const parentElement = range.commonAncestorContainer.nodeType === 3 
        ? range.commonAncestorContainer.parentElement 
        : range.commonAncestorContainer;
      
      if (tag === 'p') {
        document.execCommand('formatBlock', false, 'p');
      } else {
        document.execCommand('formatBlock', false, tag);
      }
      
      this.$refs.contentEditor.focus();
      this.updateContent();
    },
    
    formatAlign(command) {
      document.execCommand(command, false, null);
      this.$refs.contentEditor.focus();
      this.updateContent();
    },
    
    insertList(type) {
      const listHtml = type === 'ul' 
        ? '<ul><li></li></ul>' 
        : '<ol><li></li></ol>';
      document.execCommand('insertHTML', false, listHtml);
      this.$refs.contentEditor.focus();
      this.updateContent();
    },
    
    insertLink() {
      const selection = window.getSelection();
      if (selection.toString()) {
        this.linkText = selection.toString();
      }
      this.linkDialog = true;
    },
    
    confirmLink() {
      if (this.linkUrl) {
        const target = this.linkNewTab ? ' target="_blank"' : '';
        const linkHtml = `<a href="${this.linkUrl}"${target}>${this.linkText || this.linkUrl}</a>`;
        document.execCommand('insertHTML', false, linkHtml);
        this.$refs.contentEditor.focus();
        this.updateContent();
      }
      
      // Reset dialog
      this.linkDialog = false;
      this.linkUrl = '';
      this.linkText = '';
      this.linkNewTab = true;
    },
    
    insertHorizontalRule() {
      document.execCommand('insertHorizontalRule', false, null);
      this.$refs.contentEditor.focus();
      this.updateContent();
    },
    
    clearFormatting() {
      document.execCommand('removeFormat', false, null);
      this.$refs.contentEditor.focus();
      this.updateContent();
    },
    
    handleKeydown(event) {
      // Keyboard shortcuts
      if (event.ctrlKey || event.metaKey) {
        switch(event.key) {
          case 'b':
            event.preventDefault();
            this.formatText('bold');
            break;
          case 'i':
            event.preventDefault();
            this.formatText('italic');
            break;
          case 'u':
            event.preventDefault();
            this.formatText('underline');
            break;
          case 'k':
            event.preventDefault();
            this.insertLink();
            break;
        }
      }
    },
    
    insertEmoji(emoji) {
      document.execCommand('insertText', false, emoji);
      this.$refs.contentEditor.focus();
      this.updateContent();
    },
    
    applyTemplate(template) {
      this.newMessage.title = template.title;
      this.newMessage.content = template.content;
      
      if (this.showHtmlEditor) {
        this.htmlContent = template.content;
      } else {
        if (this.$refs.contentEditor) {
          this.$refs.contentEditor.innerHTML = template.content;
          this.$refs.contentEditor.focus();
        }
      }
    },
    
    clearScheduledDateTime() {
      this.scheduledDate = null;
      this.scheduledTime = '09:00';
      this.newMessage.scheduledFor = null;
    },

    triggerImageUpload() {
      this.$refs.imageUploadInput.click();
    },

    async handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.$toast?.error('Bitte wählen Sie eine Bilddatei aus');
        return;
      }

      // Validate file size (5 MB)
      if (file.size > 5 * 1024 * 1024) {
        this.$toast?.error('Bild ist zu groß. Maximale Größe: 5 MB');
        return;
      }

      this.uploadingImage = true;
      try {
        const response = await MessageService.uploadImage(file);
        console.log('Upload response:', response.data);

        if (response.data.success && response.data.imageUrl) {
          // Insert image tag at cursor position or at the end
          const imgTag = `<img src="${response.data.imageUrl}" alt="${file.name}" style="max-width: 100%; height: auto;" />`;
          console.log('Inserting image tag:', imgTag);
          console.log('HTML Editor mode:', this.showHtmlEditor);

          if (this.showHtmlEditor) {
            // Insert in HTML editor
            const textarea = this.$el.querySelector('.html-editor textarea');
            if (textarea) {
              const cursorPos = textarea.selectionStart;
              const textBefore = this.htmlContent.substring(0, cursorPos);
              const textAfter = this.htmlContent.substring(cursorPos);
              this.htmlContent = textBefore + imgTag + textAfter;
              this.updateFromHtml();
            } else {
              this.htmlContent += imgTag;
              this.updateFromHtml();
            }
          } else {
            // Insert in visual editor
            const editor = this.$refs.contentEditor;
            if (editor) {
              // Save current selection/cursor position
              const selection = window.getSelection();
              const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

              // Focus the editor first
              editor.focus();

              if (range && editor.contains(range.commonAncestorContainer)) {
                // Restore selection and insert at cursor
                selection.removeAllRanges();
                selection.addRange(range);
                document.execCommand('insertHTML', false, imgTag);
              } else {
                // No valid cursor position, append to end
                editor.innerHTML += imgTag;
              }

              this.updateContent();
              console.log('Image inserted in visual editor');
              console.log('Editor content:', editor.innerHTML);
            } else {
              console.error('Content editor ref not found');
            }
          }

          this.$toast?.success('Bild erfolgreich hochgeladen');
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        this.$toast?.error('Fehler beim Hochladen des Bildes');
      } finally {
        this.uploadingImage = false;
        // Reset file input
        event.target.value = '';
      }
    },

    async sendMessage() {
      if (!this.$refs.messageForm.validate()) return;
      
      this.sending = true;
      try {
        // Content will be sanitized on the server side
        const messageData = {
          ...this.newMessage
        };
        
        await MessageService.createMessage(messageData);
        
        this.successSnackbar = true;
        this.resetForm();
        this.loadMessages();
      } catch (error) {
        console.error('Error sending message:', error);
        // Handle error with snackbar
      } finally {
        this.sending = false;
      }
    },
    
    resetForm() {
      this.$refs.messageForm.reset();
      if (this.$refs.contentEditor) {
        this.$refs.contentEditor.innerHTML = '';
      }
      this.newMessage = {
        title: '',
        content: '',
        importance: 'medium',
        showAsPopup: false,
        backgroundColor: '#1976D2',
        textColor: '#FFFFFF',
        recipients: ['all'],
        scheduledFor: null
      };
      this.recipientType = 'all';
      this.scheduledDate = null;
      this.scheduledTime = '09:00';
      this.enableScheduling = false;
      this.alignment = 'left';
      this.showPreview = false;
      this.showHtmlEditor = false;
      this.htmlContent = '';
    },
    
    async loadMessages() {
      this.loadingMessages = true;
      try {
        const { data } = await MessageService.getAllMessagesWithStats();
        if (data.success) {
          this.allMessages = data.messages;
        }
      } catch (error) {
        console.error('Error loading messages:', error);
      } finally {
        this.loadingMessages = false;
      }
    },
    
    async deleteMessage(message) {
      if (confirm(`Nachricht "${message.title}" wirklich löschen?`)) {
        try {
          await MessageService.deleteMessage(message._id);
          this.loadMessages();
        } catch (error) {
          console.error('Error deleting message:', error);
        }
      }
    },
    
    getImportanceColor(importance) {
      return MessageService.getImportanceColor(importance);
    },
    
    getImportanceLabel(importance) {
      return MessageService.getImportanceLabel(importance);
    },
    
    formatDate(date) {
      return MessageService.formatDate(date);
    },
    
    async loadUsers() {
      try {
        const { data } = await MessageService.getActiveUsers();
        if (data.success) {
          this.users = data.users;
        }
      } catch (error) {
        console.error('Error loading users:', error);
        // Fallback to empty array
        this.users = [];
      }
    },
    
    // New methods for message details
    async openMessageDetails(message) {
      // Get detailed message info with read status
      try {
        const { data } = await MessageService.getMessageDetails(message._id);
        if (data.success) {
          this.selectedMessage = data.message;
          this.detailsDialog = true;
        }
      } catch (error) {
        console.error('Error loading message details:', error);
        // Fallback to basic message data
        this.selectedMessage = message;
        this.detailsDialog = true;
      }
    },
    
    // New methods for resend functionality
    resendMessage(message) {
      this.loadMessageToEditor(message);
    },
    
    resendFromDetails() {
      this.loadMessageToEditor(this.selectedMessage);
      this.detailsDialog = false;
    },
    
    loadMessageToEditor(message) {
      // Reset form first
      this.resetForm();
      
      // Load message data into editor
      this.newMessage.title = message.title;
      this.newMessage.content = message.content;
      this.newMessage.importance = message.importance;
      this.newMessage.showAsPopup = message.showAsPopup;
      this.newMessage.backgroundColor = message.backgroundColor || '#1976D2';
      this.newMessage.textColor = message.textColor || '#FFFFFF';
      
      // Update editors
      if (this.showHtmlEditor) {
        this.htmlContent = message.content;
      } else {
        this.$nextTick(() => {
          if (this.$refs.contentEditor) {
            this.$refs.contentEditor.innerHTML = message.content;
          }
        });
      }
      
      // Handle recipients
      if (message.recipients.includes('all')) {
        this.recipientType = 'all';
        this.newMessage.recipients = ['all'];
      } else {
        this.recipientType = 'specific';
        this.newMessage.recipients = [...message.recipients];
      }
      
      // Scroll to form
      this.$nextTick(() => {
        const formElement = this.$refs.messageForm.$el;
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        
        // Focus on title field
        const titleField = formElement.querySelector('input[type="text"]');
        if (titleField) {
          titleField.focus();
        }
      });
      
      // Show notification
      this.infoSnackbarText = 'Nachricht wurde in den Editor geladen. Sie können sie bearbeiten und erneut senden.';
      this.infoSnackbar = true;
    }
  },

  mounted() {
    this.loadMessages();
    this.loadUsers();
  }
};
</script>

<style scoped>
.content-editor {
  min-height: 200px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 12px;
  margin-top: 8px;
  font-size: 16px;
  line-height: 1.5;
  overflow-y: auto;
  max-height: 300px;
}

.content-editor:focus {
  outline: 2px solid #1976D2;
  border-color: #1976D2;
}

.editor-toolbar {
  border: 1px solid #ccc;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  padding: 8px;
  background-color: #f5f5f5;
}

.color-preview {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid #ccc;
}

.message-content {
  min-height: 100px;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
}

.message-content p {
  margin-bottom: 0.5em;
}

.message-content ul,
.message-content ol {
  margin-left: 20px;
  margin-bottom: 0.5em;
}

/* Enhanced Editor Styles */
.editor-toolbar {
  border: 1px solid #e0e0e0;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  padding: 8px;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.editor-toolbar .v-divider--vertical {
  height: 24px;
  margin: 0 8px;
}

.content-editor h3 {
  font-size: 1.5em;
  font-weight: bold;
  margin: 0.5em 0;
}

.content-editor h4 {
  font-size: 1.2em;
  font-weight: bold;
  margin: 0.5em 0;
}

.content-editor a {
  color: #1976D2;
  text-decoration: underline;
}

.content-editor hr {
  margin: 1em 0;
  border: none;
  border-top: 1px solid #e0e0e0;
}

/* Preview Pane */
.preview-pane {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.preview-header {
  padding: 8px 12px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.preview-content {
  padding: 12px;
  flex: 1;
  overflow-y: auto;
  min-height: 200px;
  max-height: 300px;
}

.preview-content h3 {
  font-size: 1.5em;
  font-weight: bold;
  margin: 0.5em 0;
}

.preview-content h4 {
  font-size: 1.2em;
  font-weight: bold;
  margin: 0.5em 0;
}

.preview-content a {
  color: #1976D2;
  text-decoration: underline;
}

.preview-content hr {
  margin: 1em 0;
  border: none;
  border-top: 1px solid #e0e0e0;
}

/* Editor Footer */
.editor-footer {
  text-align: right;
  padding: 0 4px;
}

/* HTML Editor */
.html-editor {
  font-family: 'Courier New', Courier, monospace !important;
}

.html-editor .v-input__control {
  font-family: inherit !important;
}

.html-editor textarea {
  font-family: inherit !important;
  line-height: 1.5;
}

/* Emoji Grid */
.emoji-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 4px;
}

.emoji-btn {
  min-width: 40px !important;
  height: 40px;
  font-size: 20px;
}

.emoji-btn:hover {
  background-color: #e3f2fd;
}

/* Responsive */
@media (max-width: 960px) {
  .editor-toolbar {
    gap: 2px;
  }
  
  .editor-toolbar .v-btn {
    min-width: 36px !important;
  }
  
  .emoji-grid {
    grid-template-columns: repeat(5, 1fr);
  }
}
</style>  